import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import isNil from 'lodash/isNil';

/**
 * A hook that provides safe access to a single LaunchDarkly flag by key.
 * Returns the flag value only when the LaunchDarkly client is available.
 *
 * Note: This hook assumes that the flag is a boolean.
 * Upgrade this hook to handle other types of flags as needed (string, number, json).
 */
export function useReadyFlag(flagName: string): boolean {
  const flags = useFlags();
  const ldClient = useLDClient();
  const isReady: boolean = !isNil(ldClient);
  return isReady ? Boolean(flags[flagName]) : false;
}

/**
 * A hook that provides safe access to multiple LaunchDarkly flags by keys.
 * Returns an object with flag values keyed by flag name, only when the LaunchDarkly client is available.
 *
 * Note: This hook assumes that all flags are boolean.
 * Upgrade this hook to handle other types of flags as needed (string, number, json).
 */
export function useReadyFlags(flagNames: string[]): Record<string, boolean> {
  const flags = useFlags();
  const ldClient = useLDClient();
  const isReady: boolean = !isNil(ldClient);
  return flagNames.reduce((acc, name) => {
    acc[name] = isReady ? Boolean(flags[name]) : false;
    return acc;
  }, {} as Record<string, boolean>);
}